import type React from 'react';
import type { components } from '../../../types/learnerApi';

// ─── Shared view prop shape ──────────────────────────────────────────────

export interface ModuleViewProps<TItem, TAnswer> {
  item: TItem;
  mode: 'learner' | 'preview';
  value?: TAnswer;
  onChange?: (value: TAnswer) => void;
  disabled?: boolean;
}

// ─── Answer type union ───────────────────────────────────────────────────

export type EvalAnswer =
  | { selected_ids: string[] }
  | { selected_id: string }
  | { text: string }
  | { answers: Record<string, string> }
  | { ordered_ids: string[] }
  | { matches: Record<string, string> }
  | { value: number }
  | { value: boolean }
  | { code: string };

export type FeedbackAnswer = { rating: number } | { text: string };

// ─── Evaluation registry entry ───────────────────────────────────────────

type EvaluationItemType = components['schemas']['EvaluationItemType'];
type SanitizedItem = components['schemas']['SanitizedEvaluationItem'];

export interface EvalViewEntry {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  View: React.ComponentType<ModuleViewProps<any, any>>;
  defaultAnswer: () => EvalAnswer;
  validate: (answer: EvalAnswer | undefined) => boolean;
}

// ─── Feedback registry entry ─────────────────────────────────────────────

type FeedbackItemType = components['schemas']['FeedbackItemType'];
type FeedbackItem = components['schemas']['FeedbackItem'];

export interface FeedbackViewEntry {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  View: React.ComponentType<ModuleViewProps<any, any>>;
  defaultAnswer: () => FeedbackAnswer;
  validate: (answer: FeedbackAnswer | undefined, item: FeedbackItem) => boolean;
}

// ─── Lazy imports (split by route, not by type) ──────────────────────────

import MultipleChoiceView from './evaluation/MultipleChoiceView';
import SingleChoiceView from './evaluation/SingleChoiceView';
import BooleanTrueFalseView from './evaluation/BooleanTrueFalseView';
import ShortAnswerView from './evaluation/ShortAnswerView';
import CompletionFillBlankView from './evaluation/CompletionFillBlankView';
import FreeTextEssayView from './evaluation/FreeTextEssayView';
import OrderingSequenceView from './evaluation/OrderingSequenceView';
import TupleMatchingView from './evaluation/TupleMatchingView';
import NumericInputView from './evaluation/NumericInputView';
import CodeSubmissionView from './evaluation/CodeSubmissionView';
import LikertScaleView from './feedback/LikertScaleView';
import FreeInputFeedbackView from './feedback/FreeInputFeedbackView';

// ─── Evaluation registry ─────────────────────────────────────────────────

const EVAL_REGISTRY: Record<EvaluationItemType, EvalViewEntry> = {
  multiple_choice: {
    View: MultipleChoiceView,
    defaultAnswer: () => ({ selected_ids: [] }),
    validate: (a) => !!(a && 'selected_ids' in a && (a as { selected_ids: string[] }).selected_ids.length > 0),
  },
  single_choice: {
    View: SingleChoiceView,
    defaultAnswer: () => ({ selected_id: '' }),
    validate: (a) => !!(a && 'selected_id' in a && (a as { selected_id: string }).selected_id),
  },
  boolean_true_false: {
    View: BooleanTrueFalseView,
    defaultAnswer: () => ({ value: false as boolean }),
    validate: (a) => a != null && 'value' in a,
  },
  short_answer_text: {
    View: ShortAnswerView,
    defaultAnswer: () => ({ text: '' }),
    validate: (a) => !!(a && 'text' in a && (a as { text: string }).text.trim()),
  },
  completion_fill_blank: {
    View: CompletionFillBlankView,
    defaultAnswer: () => ({ answers: {} }),
    validate: (a) => !!(a && 'answers' in a && Object.keys((a as { answers: Record<string, string> }).answers).length > 0),
  },
  free_text_essay: {
    View: FreeTextEssayView,
    defaultAnswer: () => ({ text: '' }),
    validate: (a) => !!(a && 'text' in a && (a as { text: string }).text.trim()),
  },
  ordering_sequence: {
    View: OrderingSequenceView,
    defaultAnswer: () => ({ ordered_ids: [] }),
    validate: (a) => !!(a && 'ordered_ids' in a && (a as { ordered_ids: string[] }).ordered_ids.length > 0),
  },
  tuple_matching: {
    View: TupleMatchingView,
    defaultAnswer: () => ({ matches: {} }),
    validate: (a) => !!(a && 'matches' in a && Object.keys((a as { matches: Record<string, string> }).matches).length > 0),
  },
  numeric_input: {
    View: NumericInputView,
    defaultAnswer: () => ({ value: 0 as number }),
    validate: (a) => a != null && 'value' in a,
  },
  code_submission: {
    View: CodeSubmissionView,
    defaultAnswer: () => ({ code: '' }),
    validate: (a) => !!(a && 'code' in a && (a as { code: string }).code.trim()),
  },
};

export function getEvalViewEntry(type: EvaluationItemType): EvalViewEntry {
  const entry = EVAL_REGISTRY[type];
  if (!entry) throw new Error(`No view registered for evaluation type: ${type}`);
  return entry;
}

export function getDefaultEvalAnswer(item: SanitizedItem): EvalAnswer {
  return getEvalViewEntry(item.type).defaultAnswer();
}

// ─── Feedback registry ───────────────────────────────────────────────────

const FEEDBACK_REGISTRY: Record<FeedbackItemType, FeedbackViewEntry> = {
  likert_3_scale: {
    View: LikertScaleView,
    defaultAnswer: () => ({ rating: 0 }),
    validate: (a, item) => !!(a && 'rating' in a && (a as { rating: number }).rating > 0) || !item.required,
  },
  likert_5_scale: {
    View: LikertScaleView,
    defaultAnswer: () => ({ rating: 0 }),
    validate: (a, item) => !!(a && 'rating' in a && (a as { rating: number }).rating > 0) || !item.required,
  },
  likert_10_scale: {
    View: LikertScaleView,
    defaultAnswer: () => ({ rating: 0 }),
    validate: (a, item) => !!(a && 'rating' in a && (a as { rating: number }).rating > 0) || !item.required,
  },
  free_input_feedback: {
    View: FreeInputFeedbackView,
    defaultAnswer: () => ({ text: '' }),
    validate: (a, item) => !item.required || !!(a && 'text' in a && (a as { text: string }).text.trim()),
  },
};

export function getFeedbackViewEntry(type: FeedbackItemType): FeedbackViewEntry {
  const entry = FEEDBACK_REGISTRY[type];
  if (!entry) throw new Error(`No view registered for feedback type: ${type}`);
  return entry;
}

// ─── Content view helpers ────────────────────────────────────────────────

export { default as RichTextView } from './RichTextView';
export { default as ImageView } from './ImageView';
export { default as VideoView } from './VideoView';
export { default as PdfView } from './PdfView';
export { default as ScormView } from './ScormView';
export { default as LinkView } from './LinkView';
export { default as CodeFileView } from './CodeFileView';
export { default as QuizResultView } from './QuizResultView';
